'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

export type ProductLightboxProps = {
  /** Full CF variant URLs, shown uncropped (NOT square-cropped). */
  photos: string[]
  /** Parallel to `photos`. Each entry is the media_item's alt_text or null. */
  alts?: Array<string | null>
  /** Which photo to open on. */
  initialIndex: number
  onClose: () => void
}

const SWIPE_THRESHOLD = 50

export function ProductLightbox({ photos, alts, initialIndex, onClose }: ProductLightboxProps) {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, photos.length - 1)),
  )
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const touchStartX = useRef<number | null>(null)

  const hasMany = photos.length > 1
  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length])
  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length])

  // Adjacent photos to warm so prev/next navigation is instant. Deduped (a
  // 2-photo gallery has the same neighbor on both sides) and excludes the
  // currently-shown index.
  const neighborIndices = hasMany
    ? [...new Set([(index + 1) % photos.length, (index - 1 + photos.length) % photos.length])].filter(
        (i) => i !== index,
      )
    : []

  // Keyboard: Escape closes, arrows navigate.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft' && photos.length > 1) prev()
      else if (e.key === 'ArrowRight' && photos.length > 1) next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [photos.length, onClose, prev, next])

  // Lock body scroll while open; restore on unmount.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [])

  // Move focus into the dialog on open; restore to the trigger on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    closeBtnRef.current?.focus()
    return () => {
      previouslyFocused?.focus?.()
    }
  }, [])

  if (photos.length === 0) return null

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current == null || !hasMany) return
    const delta = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current
    if (delta > SWIPE_THRESHOLD) prev()
    else if (delta < -SWIPE_THRESHOLD) next()
    touchStartX.current = null
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Product image viewer"
      data-component="ProductLightbox"
    >
      <button
        ref={closeBtnRef}
        onClick={onClose}
        className="absolute top-4 right-4 text-white/90 hover:text-white transition-colors"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {hasMany && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            prev()
          }}
          className="absolute left-2 sm:left-4 text-white/90 hover:text-white transition-colors p-2"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      <Image
        src={photos[index]}
        alt={alts?.[index] ?? ''}
        width={1600}
        height={1200}
        className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
        priority
      />

      {hasMany && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            next()
          }}
          className="absolute right-2 sm:right-4 text-white/90 hover:text-white transition-colors p-2"
          aria-label="Next image"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {hasMany && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm tabular-nums">
          {index + 1} / {photos.length}
        </div>
      )}

      {/* Preload the prev/next photos so stepping through is instant. `priority`
          injects a <link rel="preload"> that fetches regardless of the hidden
          render; matching width/height/no-sizes keeps the optimized URL
          identical to the visible <Image> above, so navigation is a cache hit. */}
      {neighborIndices.map((i) => (
        <Image
          key={`preload-${i}`}
          src={photos[i]}
          alt=""
          width={1600}
          height={1200}
          className="hidden"
          priority
          aria-hidden
        />
      ))}
    </div>
  )
}
